import type {ReactElement} from 'react'
import type {ArrowGeometry} from '../../lib/lvs-arrow'
import {Arrow} from '../../lib/lvs-arrow'
import {LVS_COLORS} from '../../lib/lvs-colors'
import {ANNOTATION} from '../../lib/annotation-inks'
import {round4} from '../../lib/utils'
import {HorizDim, VertDim} from '../ui/dimension-line'
import type {ArrowFillKey} from './ArrowControls'

const SCALE = 0.4
const PAD_X = 80
const PAD_TOP = 60
const PAD_BOTTOM = 110
/** Extra horizontal scratch on the leading side for the 2.5h reference bar. */
const REF_GUTTER = 24

/**
 * Arrows on real LVS signs sit on either a white panel (black arrow) or
 * a blue/green panel (white arrow). The canvas mirrors that pairing so
 * the rendered arrow is always visible against its panel.
 */
const PANEL_BG: Readonly<Record<ArrowFillKey, string>> = {
    white: LVS_COLORS.blue.hex,
    black: LVS_COLORS.white.hex
}

export interface ArrowCanvasProps {
    readonly geometry: ArrowGeometry
    readonly fill: ArrowFillKey
    /** Tinted overlay for the A-unit lattice. Defaults to true. */
    readonly showGrid?: boolean
}

/**
 * Scaled, annotated preview of a single LVS figure 9 arrow. Mirrors the
 * FoniCanvas / GlyphInspector annotation style: black-filled arrow on a
 * surface-container-highest panel, dimension lines for total length, head
 * length, head base, stem width, and barb-back, plus a 2.5h reference
 * marker that turns red when the arrow falls below sign 705's minimum.
 */
export function ArrowCanvas({geometry, fill, showGrid = true}: ArrowCanvasProps) {
    const {bbox, A, h, totalLength, stemLength, headLength, headBase, stemWidth, barbBack} =
        geometry
    const min705 = 2.5 * h
    const meets705 = totalLength + 1e-9 >= min705

    const panelBg = PANEL_BG[fill]
    // Lattice grid: subtle white-on-blue when the panel is dark, otherwise
    // the canonical light guide ink.
    const gridColor = fill === 'white' ? '#ffffff' : ANNOTATION.guide
    const gridOpacity = fill === 'white' ? 0.18 : 0.6

    const innerWidth = bbox.width
    const innerHeight = bbox.height
    // Leading-side gutter must accommodate the 2.5h reference bar when it
    // extends past the arrow tip (i.e. when total < 2.5h).
    const refOverhang = Math.max(0, min705 - totalLength) * SCALE
    const svgW = innerWidth * SCALE + 2 * PAD_X + REF_GUTTER + refOverhang
    const svgH = innerHeight * SCALE + PAD_TOP + PAD_BOTTOM

    // Inside the SVG, place the arrow with its bbox.x at PAD_X (post-scale).
    // We translate to the arrow's local origin (stem-tail center) so the
    // bbox top-left lands at (PAD_X, PAD_TOP).
    const offsetX = PAD_X - bbox.x * SCALE
    const offsetY = PAD_TOP - bbox.y * SCALE

    return (
        <div className='overflow-x-auto border border-foreground/10 bg-surface-container-highest'>
            <svg
                width={svgW}
                height={svgH}
                style={{display: 'block'}}
            >
                {/* Sign-panel background: only behind the arrow's bbox so
                    that dimension annotations stay on the light surface. */}
                <rect
                    x={PAD_X}
                    y={PAD_TOP}
                    width={innerWidth * SCALE}
                    height={innerHeight * SCALE}
                    fill={panelBg}
                />
                <g transform={`translate(${offsetX} ${offsetY}) scale(${SCALE})`}>
                    {showGrid && (
                        <ALattice
                            bbox={bbox}
                            A={A}
                            color={gridColor}
                            opacity={gridOpacity}
                        />
                    )}
                    <Arrow
                        geometry={geometry}
                        fill={fill}
                    />
                </g>

                {/* Total length dimension across the arrow. */}
                <HorizDim
                    x1={PAD_X}
                    x2={PAD_X + innerWidth * SCALE}
                    y={PAD_TOP - 24}
                    label={`L = ${round4(totalLength)} mm (${round4(totalLength / h)}h)`}
                    clr={ANNOTATION.dim}
                    labelOffset={8}
                />

                {/* Head length dimension across the head segment only. */}
                <HeadLengthDim
                    geometry={geometry}
                    panelTop={PAD_TOP}
                    panelInnerWidth={innerWidth}
                />

                {/* Stem width dimension on the left side. */}
                <VertDim
                    x={PAD_X - 24}
                    y1={PAD_TOP + innerHeight * SCALE - (1.5 * A - stemWidth / 2) * SCALE}
                    y2={PAD_TOP + innerHeight * SCALE - (1.5 * A + stemWidth / 2) * SCALE}
                    label={`1A = ${round4(stemWidth)} mm`}
                    clr={ANNOTATION.muted}
                    labelOffset={10}
                />

                {/* Head base (3A) on the right side of the panel. */}
                <VertDim
                    x={PAD_X + innerWidth * SCALE + 14}
                    y1={PAD_TOP}
                    y2={PAD_TOP + innerHeight * SCALE}
                    label={`3A = ${round4(headBase)} mm`}
                    clr={ANNOTATION.dim}
                    labelOffset={10}
                />

                {/* 2.5h §705 minimum reference bar below the arrow. */}
                <Min705Bar
                    geometry={geometry}
                    panelInnerHeight={innerHeight}
                    meets={meets705}
                    min={min705}
                />
            </svg>
            <p className='border-t border-foreground/10 px-4 py-2 font-mono text-[11px] text-foreground/60'>
                A = {round4(A)} mm · stems = {round4(stemLength)} mm ({geometry.stemA}A) · galva ={' '}
                {round4(headLength)} mm (3A) · barbu atkāpe = {round4(barbBack)} mm (0.25A)
            </p>
        </div>
    )
}

interface ALatticeProps {
    readonly bbox: {
        readonly x: number
        readonly y: number
        readonly width: number
        readonly height: number
    }
    readonly A: number
    readonly color: string
    readonly opacity: number
}

/**
 * Light A-unit grid behind the arrow. Lines snap to multiples of A in
 * the geometry's local frame so caller doesn't have to know the scale.
 */
function ALattice({bbox, A, color, opacity}: ALatticeProps) {
    const lines: ReactElement[] = []
    const xStart = Math.floor(bbox.x / A) * A
    const xEnd = Math.ceil((bbox.x + bbox.width) / A) * A
    const yStart = Math.floor(bbox.y / A) * A
    const yEnd = Math.ceil((bbox.y + bbox.height) / A) * A
    for (let x = xStart; x <= xEnd + 1e-6; x += A) {
        lines.push(
            <line
                key={`vx-${x}`}
                x1={x}
                y1={bbox.y}
                x2={x}
                y2={bbox.y + bbox.height}
                stroke={color}
                strokeWidth={0.5}
                opacity={opacity}
            />
        )
    }
    for (let y = yStart; y <= yEnd + 1e-6; y += A) {
        lines.push(
            <line
                key={`hy-${y}`}
                x1={bbox.x}
                y1={y}
                x2={bbox.x + bbox.width}
                y2={y}
                stroke={color}
                strokeWidth={0.5}
                opacity={opacity}
            />
        )
    }
    return <g>{lines}</g>
}

interface HeadLengthDimProps {
    readonly geometry: ArrowGeometry
    readonly panelTop: number
    readonly panelInnerWidth: number
}

/**
 * Dimension line under the arrow body covering only the head (notch
 * floor → tip). The notch sits at stemLength forward of the bbox's
 * leading edge for both directions; we project that to the panel.
 */
function HeadLengthDim({geometry, panelTop, panelInnerWidth}: HeadLengthDimProps) {
    const {direction, stemLength, headLength} = geometry
    const panelLeft = PAD_X
    const panelRight = panelLeft + panelInnerWidth * SCALE
    const yBelow = panelTop + geometry.bbox.height * SCALE + 18

    const x1 = direction === 'right' ? panelLeft + stemLength * SCALE : panelLeft
    const x2 = direction === 'right' ? panelRight : panelLeft + headLength * SCALE
    return (
        <HorizDim
            x1={x1}
            x2={x2}
            y={yBelow}
            label={`3A = ${round4(headLength)} mm`}
            clr={ANNOTATION.dim}
            labelAbove={false}
            labelOffset={14}
        />
    )
}

interface Min705BarProps {
    readonly geometry: ArrowGeometry
    readonly panelInnerHeight: number
    readonly meets: boolean
    readonly min: number
}

/**
 * 2.5h reference bar (sign 705 minimum). Bar starts at the arrow's
 * trailing edge and extends `min` mm forward; when the arrow's tip
 * crosses it, sign 705's minimum is met. The bar turns red when not.
 */
function Min705Bar({geometry, panelInnerHeight, meets, min}: Min705BarProps) {
    const {direction, bbox} = geometry
    const yBar = PAD_TOP + panelInnerHeight * SCALE + 44
    const tail = direction === 'right' ? PAD_X : PAD_X + bbox.width * SCALE
    const head = direction === 'right' ? tail + min * SCALE : tail - min * SCALE
    const color = meets ? ANNOTATION.ok : ANNOTATION.warn

    const x1 = Math.min(tail, head)
    const x2 = Math.max(tail, head)
    return (
        <g>
            <HorizDim
                x1={x1}
                x2={x2}
                y={yBar}
                label={`2.5h min (705. zīme) = ${round4(min)} mm`}
                clr={color}
                labelAbove={false}
                labelOffset={14}
                strokeWidth={1.5}
            />
        </g>
    )
}
